import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  PurchaseOrder,
  lineCostTotal,
  lineRetailTotal,
  lineUnitTotal,
  markupMultiple,
} from '../../types/purchasing';

interface PurchaseOrderSummaryProps {
  order: PurchaseOrder;
  saving?: boolean;
  onSaveDraft: () => void;
  onSubmit: () => void;
  readOnly?: boolean;
}

const formatMoney = (value: number, currency: string) =>
  new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);

const PurchaseOrderSummary: React.FC<PurchaseOrderSummaryProps> = ({
  order,
  saving,
  onSaveDraft,
  onSubmit,
  readOnly = false,
}) => {
  const productCount = order.lines.length;
  const totalUnits = order.lines.reduce(
    (sum, line) => sum + lineUnitTotal(line),
    0,
  );
  const totalCost = order.lines.reduce(
    (sum, line) => sum + lineCostTotal(line),
    0,
  );
  const totalRetail = order.lines.reduce(
    (sum, line) => sum + lineRetailTotal(line),
    0,
  );
  const avgMarkup = markupMultiple(totalCost, totalRetail) ?? 0;

  const totalsByMonth = useMemo(() => {
    const acc: Record<string, { units: number; cost: number }> = {};
    for (const line of order.lines) {
      for (const sku of line.skus) {
        const month =
          sku.deliveryMonth || line.deliveryMonth || order.defaultDeliveryMonth;
        if (!month) continue;
        if (!acc[month]) acc[month] = { units: 0, cost: 0 };
        const qty = Number(sku.quantity || 0);
        acc[month].units += qty;
        acc[month].cost += qty * Number(sku.supplierPrice || 0);
      }
    }
    return Object.entries(acc).sort(([a], [b]) => a.localeCompare(b));
  }, [order]);

  return (
    <Card sx={{ position: 'sticky', top: 16 }}>
      <CardHeader
        title="Ordersammanställning"
        subheader={
          order.supplierOrderNumber?.trim()
            ? `Ordernr ${order.orderNumber} · Lev. ordernr ${order.supplierOrderNumber.trim()}`
            : `Ordernr ${order.orderNumber}`
        }
      />
      <CardContent>
        <Stack spacing={1}>
          <Stat label="Produkter" value={String(productCount)} />
          <Stat
            label="Antal styck"
            value={totalUnits.toLocaleString('sv-SE')}
          />
          <Divider sx={{ my: 1 }} />
          <Stat
            label="Inköpsvärde"
            value={formatMoney(totalCost, order.currency)}
          />
          <Stat
            label="Försäljningsvärde"
            value={formatMoney(totalRetail, order.currency)}
          />
          <Stat
            label="Snittmarkup"
            value={totalRetail && totalCost ? `${avgMarkup.toFixed(2)}×` : '-'}
          />
        </Stack>

        {totalsByMonth.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Per leveransmånad
            </Typography>
            <Stack spacing={0.5}>
              {totalsByMonth.map(([month, data]) => (
                <Box
                  key={month}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Typography variant="body2">{month}</Typography>
                  <Typography variant="body2">
                    {data.units} st · {formatMoney(data.cost, order.currency)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {!readOnly && (
          <Stack spacing={1.5} sx={{ mt: 3 }}>
            {order.status === 'draft' && (
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={saving || totalUnits <= 0}
              >
                Skapa order
              </Button>
            )}
            <Button variant="outlined" onClick={onSaveDraft} disabled={saving}>
              {order.status === 'draft' ? 'Spara utkast' : 'Spara ändringar'}
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

export default PurchaseOrderSummary;
